import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Topbar from "../Global/topbar";
import CachedIcon from "@mui/icons-material/Cached";
import { RiFileExcel2Line } from "react-icons/ri";
import {
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { getDailyCallReport } from "../../services/api/dailyCallReport";
import { exportToExcel } from "../Global/exportToExcel";
import "../Audio Type/audio.css";
import "./callReport.css";

const istYesterdayKey = () => {
  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
  const istNow = new Date(Date.now() + IST_OFFSET_MS);
  const y = new Date(istNow);
  y.setUTCDate(y.getUTCDate() - 1);
  return y.toISOString().slice(0, 10);
};

const istTodayKey = () => {
  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
  const istNow = new Date(Date.now() + IST_OFFSET_MS);
  return istNow.toISOString().slice(0, 10);
};

const formatSec = (s) => {
  const n = Number(s) || 0;
  if (n <= 0) return "0:00";
  const m = Math.floor(n / 60);
  const r = Math.floor(n % 60);
  return `${m}:${String(r).padStart(2, "0")}`;
};

export default function CallReport() {
  const today = istTodayKey();
  const [date, setDate] = useState(istYesterdayKey());
  const [loader, setLoader] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [rows, setRows] = useState([]);
  const [reload, setReload] = useState(true);

  const fetchReport = async () => {
    setLoader(true);
    setNotFound(false);
    try {
      const response = await getDailyCallReport(date);
      if (response?.data?.status === "success") {
        const list = response?.data?.data?.data || [];
        if (list.length === 0) {
          setRows([]);
          setNotFound(true);
        } else {
          // Sort by total_calls desc by default so the most active partners
          // surface first.
          const sorted = [...list].sort(
            (a, b) => Number(b.total_calls || 0) - Number(a.total_calls || 0),
          );
          setRows(sorted);
        }
      } else {
        setNotFound(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [date, reload]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.total_calls += Number(r.total_calls || 0);
        acc.answered += Number(r.answered || 0);
        acc.missed += Number(r.missed || 0);
        acc.rejected += Number(r.rejected || 0);
        acc.total_talktime_sec += Number(r.total_talktime_sec || 0);
        return acc;
      },
      {
        total_calls: 0,
        answered: 0,
        missed: 0,
        rejected: 0,
        total_talktime_sec: 0,
      },
    );
  }, [rows]);

  return (
    <div className="audio-main">
      <div className="audio-user-top">
        <Topbar />
        <div className="top-mid-audio">
          <h1>Call Report</h1>
          <Link to="/admin/call_report">Call Report</Link> &#62;
        </div>

        <div className="report-toolbar">
          <div className="report-date-picker">
            <label>Date</label>
            <input
              type="date"
              value={date}
              max={today}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <button
            className="report-reload"
            onClick={() => setReload((p) => !p)}
            title="Reload"
          >
            <CachedIcon />
          </button>
          <button
            className="report-download"
            disabled={!rows.length}
            onClick={() => {
              exportToExcel(
                rows,
                [
                  { header: "Consultant ID", key: "consultant_id" },
                  { header: "Total Calls", key: "total_calls" },
                  { header: "Dialed", key: "dialed" },
                  { header: "Received", key: "received" },
                  { header: "Answered", key: "answered" },
                  { header: "Missed", key: "missed" },
                  { header: "Missed by User", key: "missed_by_user" },
                  { header: "Missed by Partner", key: "missed_by_partner" },
                  { header: "Rejected", key: "rejected" },
                  { header: "Talktime (sec)", key: "total_talktime_sec" },
                  { header: "Avg Talktime (sec)", key: "avg_talktime_sec" },
                  { header: "Max Talktime (sec)", key: "max_talktime_sec" },
                  { header: "First Call At", key: "first_call_at" },
                  { header: "Last Call At", key: "last_call_at" },
                ],
                `call-report-${date}.xlsx`,
              );
            }}
            title={rows.length ? "Download as Excel" : "No data to download"}
          >
            <RiFileExcel2Line style={{ fontSize: "1.1rem" }} />
            <span>Download</span>
          </button>
        </div>

        {/* Day-level summary tiles */}
        {!loader && !notFound && rows.length > 0 ? (
          <div className="report-summary">
            <div className="report-tile">
              <div className="tile-label">Total Calls</div>
              <div className="tile-value">{totals.total_calls}</div>
            </div>
            <div className="report-tile">
              <div className="tile-label">Answered</div>
              <div className="tile-value">{totals.answered}</div>
            </div>
            <div className="report-tile">
              <div className="tile-label">Missed</div>
              <div className="tile-value">{totals.missed}</div>
            </div>
            <div className="report-tile">
              <div className="tile-label">Rejected</div>
              <div className="tile-value">{totals.rejected}</div>
            </div>
            <div className="report-tile">
              <div className="tile-label">Talktime</div>
              <div className="tile-value">
                {formatSec(totals.total_talktime_sec)}
              </div>
            </div>
            <div className="report-tile">
              <div className="tile-label">Consultants</div>
              <div className="tile-value">{rows.length}</div>
            </div>
          </div>
        ) : null}

        <div className="report-table-wrap">
          {loader ? (
            <div className="report-loader">
              <CircularProgress size={28} />
            </div>
          ) : notFound ? (
            <div className="report-empty">
              No call report found for <b>{date}</b>. Reports are generated
              nightly at 12:00 AM IST.
            </div>
          ) : (
            <TableContainer component={Paper} className="report-table">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Consultant ID</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Dialed</TableCell>
                    <TableCell>Received</TableCell>
                    <TableCell>Answered</TableCell>
                    <TableCell>Missed</TableCell>
                    <TableCell>Missed by User</TableCell>
                    <TableCell>Missed by Partner</TableCell>
                    <TableCell>Rejected</TableCell>
                    <TableCell>Talktime</TableCell>
                    <TableCell>Avg</TableCell>
                    <TableCell>Max</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={r.id || r.SK || r.consultant_id || i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        <code className="report-cid">{r.consultant_id}</code>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={r.total_calls || 0}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{r.dialed || 0}</TableCell>
                      <TableCell>{r.received || 0}</TableCell>
                      <TableCell>
                        <span className="cell-positive">
                          {r.answered || 0}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="cell-warning">{r.missed || 0}</span>
                      </TableCell>
                      <TableCell>{r.missed_by_user || 0}</TableCell>
                      <TableCell>{r.missed_by_partner || 0}</TableCell>
                      <TableCell>
                        <span className="cell-negative">
                          {r.rejected || 0}
                        </span>
                      </TableCell>
                      <TableCell>
                        {formatSec(r.total_talktime_sec)}
                      </TableCell>
                      <TableCell>{formatSec(r.avg_talktime_sec)}</TableCell>
                      <TableCell>{formatSec(r.max_talktime_sec)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      </div>
    </div>
  );
}
